/**
 * WorkingCapitalSection Component
 * Collapsible working capital with liability and asset drill-down.
 * Fetches Balance Sheet for line-item detail.
 */

import { useState, useEffect } from 'react';
import { SectionCard } from './SectionCard';
import { formatCurrency } from '@/utils/formatters';
import { apiClient } from '@/api/apiClient';
import type { CashPositionResponse } from '@/api/cashApi';

interface WorkingCapitalSectionProps {
  workingCapital: CashPositionResponse['data']['workingCapital'];
}

interface LineItem {
  name: string;
  amount: number;
}

export function WorkingCapitalSection({ workingCapital }: WorkingCapitalSectionProps) {
  const [liabilities, setLiabilities] = useState<LineItem[]>([]);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (!showDetail || liabilities.length > 0) return;
    apiClient.get('/reports/balance-sheet')
      .then((res) => {
        const data = res.data as { data: { liabilities: { items: LineItem[] } } };
        const items = data.data?.liabilities?.items ?? [];
        setLiabilities(
          items.filter((i) => Math.abs(i.amount) > 0.01)
            .sort((a, b) => b.amount - a.amount)
        );
      })
      .catch(() => { /* graceful degradation */ });
  }, [showDetail, liabilities.length]);

  const wcColor = workingCapital.workingCapital >= 0 ? 'var(--success, #2C8248)' : 'var(--error, #dc2626)';

  return (
    <SectionCard title="Working Capital">
      <div className="cash-pnl-dashboard__working-capital">
        <div className="cash-pnl-dashboard__wc-item">
          <span className="cash-pnl-dashboard__wc-label">Current Assets</span>
          <span className="cash-pnl-dashboard__wc-value">
            {formatCurrency(workingCapital.currentAssets)}
          </span>
        </div>
        <div className="cash-pnl-dashboard__wc-item">
          <span className="cash-pnl-dashboard__wc-label">
            Current Liabilities
            <button
              onClick={() => setShowDetail((p) => !p)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--marine, #075156)', fontSize: '11px', marginLeft: '4px' }}
              type="button"
            >
              {showDetail ? '(hide)' : '(detail)'}
            </button>
          </span>
          <span className="cash-pnl-dashboard__wc-value">
            {formatCurrency(workingCapital.currentLiabilities)}
          </span>
        </div>

        {showDetail && liabilities.length > 0 && (
          <div style={{ padding: '8px 0 8px 16px', fontSize: '12px', borderLeft: '2px solid var(--pale-blue, #E5EEEF)' }}>
            {liabilities.map((item) => (
              <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: '#555' }}>
                <span>{item.name}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="cash-pnl-dashboard__wc-item cash-pnl-dashboard__wc-item--total">
          <span className="cash-pnl-dashboard__wc-label">Working Capital</span>
          <span className="cash-pnl-dashboard__wc-value" style={{ color: wcColor }}>
            {formatCurrency(workingCapital.workingCapital)}
          </span>
        </div>
        <div className="cash-pnl-dashboard__wc-item">
          <span className="cash-pnl-dashboard__wc-label">Current Ratio</span>
          <span className="cash-pnl-dashboard__wc-value">
            {workingCapital.currentRatio.toFixed(2)}x
          </span>
        </div>
      </div>
    </SectionCard>
  );
}
